import ValidationError from '../../lib/error/errors/ValidationError'
import { Hook } from '../constants/Hook'
import { Event } from '../constants/Event'
import { createHash } from 'crypto'


//TODO: test this


class SaveHandler {

    constructor({ api, configurator }) {
        this.api = api
        this.configurator = configurator
    }

    /**
     * Returns the newsItem after it has been processed by the exporter
     * @returns {*|string}
     */
    getExportedDocument() {
        const exporter = this.configurator.createExporter('idf', { api: this.api })
        return exporter.exportDocument(this.api.editorSession.getDocument(), this.api.newsItemArticle)
    }

    /**
     * Runs validate method on all provided validators
     *
     * @param {array} validators - An array with Validators
     * @param document
     * @returns {array} Messages - An array with messages objects
     * @throws Error - Error is thrown is the validator class doesn't have a validate method
     */
    runValidatorsForDocument(validators, document) {
        let messages = []

        const parser = new DOMParser()
        const newsItemArticle = parser.parseFromString(document, 'text/xml')

        validators.forEach((validatorClass) => {
            const pluginValidator = new validatorClass(newsItemArticle)
            if (pluginValidator.validate) {
                pluginValidator.validate()

                if (pluginValidator.hasMessages()) {
                    messages = [...messages, ...pluginValidator.getMessages()]
                }
            } else {
                throw new Error('Validator missing validate method')
            }
        })

        return messages
    }

    /**
     * Runs all validators and shows messages in a dialog
     * Promise is resolved if user clicks continue
     * Promise is rejected if user clicks cancel
     *
     * If no messages is found in the validators the promise is resolved immediately
     * @param document Exported newsItem as XMLString
     * @returns {Promise}
     */
    validateDocument(document) {
        return new Promise((resolve, reject) => {
            const messages = this.runValidatorsForDocument(this.api.configurator.getValidators(), document)

            if (messages.length === 0) {
                resolve(document)
                return
            }

            let heading = this.getDialogHeading(this.getMessageLevel(messages), messages.length)

            this.api.ui.showMessageDialog(messages,
                () => resolve(document),
                () => reject(new ValidationError(messages)),
                heading
            )
        })
    }

    /**
     * Gets the highest level of a message in a message object or an array of message objects
     * (0 = info, 1 = warning, 2 = error)
     * @example
     * obj.getMessageLevel([
     *   {
     *     type: 'info',
     *     message: 'Informative message'
     *   },
     *   {
     *     type: 'error',
     *     message: 'Dire message'
     *   },
     * ])
     * @param {Object|Object[]} messages A single message object or an array of message objects
     * @returns number
     */
    getMessageLevel(messages) {
        if (!Array.isArray(messages)) {
            messages = [messages]
        }

        return messages.reduce((acc, cur) => {
            let val = 0
            if (cur.type === 'error') {
                val = 2
            } else if (cur.type === 'warning') {
                val = 1
            }
            return val > acc ? val : acc
        }, 0)
    }

    /**
     * Returns the default heading message for the message level specified by messageLevel
     * @param {number} messageLevel
     * @param {number} messageCount the number of messages
     */
    getDialogHeading(messageLevel, messageCount) {
        let heading = ''
        if (messageLevel === 1) {
            heading = messageCount > 1 ? 'save-warning-plural' : 'save-warning-singular'
        } else if (messageLevel === 2) {
            heading = messageCount > 1 ? 'save-error-plural' : 'save-error-singular'
        }
        return this.api.getLabel(heading)
    }

    /**
     * Execute all hooks of a certain type
     *
     * @param  {string} type Type of hook, constants defined in Hook
     * @return {Promise}
     */
    executeHooks(type) {
        const promises = []

        this.configurator.getHooks(type).forEach(hook => {
            try {
                if (!hook.class) {
                    promises.push(Promise.reject(`Found registered ${type} hook with no hook class`))
                }

                const obj = new hook.class()
                if (!obj.execute) {
                    promises.push(Promise.reject(`Registered ${type} hook ${hook.class} is missing execute method`))
                }

                const response = obj.execute(type)
                if (response && response.constructor.name === 'Promise') {
                    promises.push(response)
                }
                else {
                    console.warn(`Registered ${type} hook ${hook.class} should have returned Promise, but returned ${response.constructor.name}`)
                    promises.push(Promise.resolve(response))
                }
            }
            catch (ex) {
                promises.push(Promise.reject(ex))
            }
        })

        return Promise.all(promises)
    }

    /**
     * Walk through hooks, validation and save document
     *
     * @returns Promise}
     */
    saveDocument() {
        let uuid = null

        return Promise.resolve()
            .then(() => {
                // Before validation hooks can potentially change the document
                return this.executeHooks(Hook.BEFORE_VALIDATE)
            })
            .then(() => {
                // Validate the document and return it to the next step
                return this.validateDocument(this.getExportedDocument())
            })
            .then((doc) => {
                return this.executeHooks(Hook.BEFORE_SAVE).then(() => doc)
            })
            .then(doc => {
                // Update or create if it has no uuid
                const currentHash = this._getCurrentHash(doc)

                uuid = this.api.newsItem.getGuid()
                if (uuid) {
                    // If doc has same hash as previous "version" we do
                    // not want to make an update request to backend.
                    if (this.previousHash !== currentHash) {
                        return this.updateNewsItem(uuid, doc).then(() => {
                            this.previousHash = currentHash
                        })
                    }
                } else {
                    return this.createNewsItem(doc).then(() => {
                        this.previousHash = currentHash
                    })
                }
            })
            .then(() => {
                return this.executeHooks(Hook.AFTER_SAVE)
            })
            .then(() => {
                this.api.events.trigger(null, Event.DOCUMENT_SAVED)
            })
            .catch(error => {
                console.error(error.message)

                if (error.name === 'ValidationError') {
                    this.api.events.trigger(null, Event.USERACTION_CANCEL_SAVE, {})
                }
                else {
                    this.api.events.trigger(null, Event.DOCUMENT_SAVE_FAILED, error)
                    this.api.router.get('/api/err?error=' + JSON.stringify(error.message))
                }
            })
    }

    /**
     * Save a new NewsItem to the backend and replace the existing NewsML in
     * the Writer as the NewsItem is changed when first created. For example
     * uuid, creation date and more is added.
     *
     * The Promise is resolved when first the newsItem as been saved and then
     * fetched from the server in a separate call.
     *
     * The temporyId that existed prior to creating this newsItem will not
     * be replaced as long as api.newsItem.getIdForArticle() is around.
     *
     * @param  {string} newsItemXmlString The NewsML as a string
     * @return {Promise}
     */
    createNewsItem(newsItemXmlString) {
        let uuid = null

        return this.api.router.post('/api/newsitem', { body: newsItemXmlString })
            .then(response => this.api.router.checkForOKStatus(response))
            .then(response => response.text())
            .then((response) => {
                uuid = response
                return uuid
            })
            .then(() => this.api.router.get('/api/newsitem/' + uuid, { imType: 'x-im/article' }))
            .then(response => this.api.router.checkForOKStatus(response))
            .then(response => this.api.router.handleEtag(response, uuid))
            .then(response => response.text())
            .then((xmlString) => {
                this.api.browser.ignoreNextHashChange = true

                this.api.history.deleteHistory(this.api.newsItem.getIdForArticle())
                this.api.newsItem.setTemporaryId(null)

                this.api.browser.setHash(uuid)

                this.api.newsItem.replaceSource(xmlString)
            })
            .catch((error) => {
                throw (error)
            })
    }

    /**
     * Save existing NewsItem to the backend
     *
     * @param  {string} uuid              Uuid of this NewsItem
     * @param  {string} newsItemXmlString The NewsML as a string
     * @return {Promise}
     */
    updateNewsItem(uuid, newsItemXmlString) {
        return this.api.router.put('/api/newsitem/' + uuid, { body: newsItemXmlString, uuid: uuid })
            .then(response => this.api.router.checkForOKStatus(response))
            .then(response => this.api.router.handleEtag(response, uuid))
            .catch((error) => {
                error.uuid = uuid
                throw (error)
            })
    }

    _getCurrentHash(doc) {
        const hash = createHash('sha256')
        hash.update(doc)
        return hash.digest('hex')
    }
}

export { SaveHandler }
